import { roundHalfUp } from '@/lib/format';
import type { ExchangeRateInfo } from './stockTypes';

/**
 * 환율 탭의 계산 - 화면과 떼어 테스트한다. (2026-09-19, I-61)
 *
 * 🔴 여기 나오는 원화는 전부 "참고 환율" 기준이다
 *   토스가 주는 표시 환율이라 실제 환전 때의 거래 환율(스프레드 포함)과 다를 수 있다.
 *   화면이 그 사실을 함께 말한다. 계산기 결과를 "환전하면 받는 돈" 으로 쓰지 않는다.
 */

/**
 * 추이선 기간(일). 서버는 1~1100 을 받는다. (2026-09-20 확장, I-66)
 *
 * 🔴 30일을 넘으면 서버가 **하루 한 점**으로 줄여 준다
 *   토스 이력은 한 시간 한 점이라 1년이면 8,760점이다. 화면 폭은 그대로라
 *   선만 뭉개지고 JSON 만 커진다.
 *
 * 🔴 3년 구간의 앞부분은 한국은행 값이다
 *   토스는 지금 환율만 주고 우리 이력은 2026-08-20 21시부터다. 그 앞은
 *   한국은행 ECOS 의 일별 매매기준율로 채웠다 - 성격이 조금 다르므로 화면이 밝힌다.
 */
export type FxRange = 7 | 30 | 90 | 180 | 365 | 1095;
export const FX_RANGES: FxRange[] = [7, 30, 90, 180, 365, 1095];

/** 버튼에 적을 말. 일수를 그대로 적으면 "1095일" 이 되어 읽기 어렵다 */
export function fxRangeLabel(days: FxRange): string {
  switch (days) {
    case 7:
      return '7일';
    case 30:
      return '30일';
    case 90:
      return '3개월';
    case 180:
      return '6개월';
    case 365:
      return '1년';
    default:
      return '3년';
  }
}

/** 기간 설명에 쓸 말. "7일 전보다" 처럼 붙는다 */
export function fxRangeSince(days: FxRange): string {
  return `${fxRangeLabel(days)} 전보다`;
}

/** 이 기간에 쓸 범위 라벨. "3개월 범위" */
export function fxRangeSpan(days: FxRange): string {
  return `${fxRangeLabel(days)} 범위`;
}

/**
 * 환율 표기. 원 단위 아래 두 자리까지 적는다.
 *
 *   formatRate(1385.2)  → "1,385.20원"
 *   formatRate(1385.256) → "1,385.26원"
 *
 * 🔴 formatKrw 를 쓰지 않는 이유
 *   formatKrw 는 원 단위로 반올림한다. 환율은 소수 둘째 자리까지 움직여서
 *   반올림하면 하루 변동이 통째로 사라진다.
 */
export function formatRate(rate: number | null | undefined): string {
  if (rate === null || rate === undefined || !Number.isFinite(rate)) {
    return '-';
  }
  return `${roundHalfUp(rate, 2).toLocaleString('ko-KR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}원`;
}

/** 달러 → 원. 원 단위로 반올림한다(HALF_UP) */
export function usdToKrw(usd: number, rate: number): number | null {
  if (!Number.isFinite(usd) || !Number.isFinite(rate) || rate <= 0) {
    return null;
  }
  return roundHalfUp(usd * rate, 0);
}

/** 원 → 달러. 센트(소수 둘째 자리)로 반올림한다(HALF_UP) */
export function krwToUsd(krw: number, rate: number): number | null {
  if (!Number.isFinite(krw) || !Number.isFinite(rate) || rate <= 0) {
    return null;
  }
  return roundHalfUp(krw / rate, 2);
}

/**
 * 달러 입력칸의 값을 다듬는다. 숫자와 소수점 하나, 소수 둘째 자리까지만 남긴다.
 *
 *   toUsdInput('1,234.567') → '1234.56'   (콤마 제거 · 셋째 자리 버림)
 *   toUsdInput('00.5')      → '0.5'
 *   toUsdInput('12.')       → '12.'       (입력 중인 소수점은 남긴다)
 *   toUsdInput('1.2.3')     → '1.23'      (두 번째 점은 버린다)
 *
 * 🔴 lib/format 의 toMoneyValue 를 쓰지 않는 이유
 *   그쪽은 원화용이라 소수를 받지 않는다(설계 의도). 달러는 센트가 있다.
 */
export function toUsdInput(raw: string): string {
  const cleaned = raw.replace(/[^0-9.]/g, '');
  const dot = cleaned.indexOf('.');
  const whole = (dot < 0 ? cleaned : cleaned.slice(0, dot)).replace(/^0+(?=\d)/, '');
  if (dot < 0) {
    return whole;
  }
  const fraction = cleaned.slice(dot + 1).replace(/\./g, '').slice(0, 2);
  return `${whole === '' ? '0' : whole}.${fraction}`;
}

export interface FxSummary {
  /** 기간 첫 점(가장 오래된 매시 기록) */
  first: number;
  /** 비교 기준이 된 지금 값 - 지금 환율이 있으면 그것, 없으면 마지막 기록 */
  current: number;
  min: number;
  max: number;
  /** current - first. 원 단위, 소수 둘째 자리 */
  change: number;
  /** change / first x 100. 소수 둘째 자리 */
  changeRate: number;
}

/**
 * 기간 요약 - "7일 전보다 얼마 올랐나" 와 기간 최저·최고.
 *
 * @param current 지금 환율. 이력은 매시 정각 값이라 지금보다 최대 1시간 늦다 -
 *                맨 위 큰 숫자와 변동 폭이 서로 다른 값을 말하지 않게 지금 값으로 잰다
 * @return 이력이 비었으면 null
 */
export function summarize(
  history: ExchangeRateInfo[],
  current: number | null | undefined,
): FxSummary | null {
  const rates = history.map((h) => h.rate);
  const first = rates[0];
  const last = rates[rates.length - 1];
  if (first === undefined || last === undefined) {
    return null;
  }
  const now = current ?? last;
  // 지금 값도 기간 안의 한 점이다 - 최저·최고에 넣지 않으면 "최고 1,380원" 옆에 1,385원이 뜬다
  const all = [...rates, now];
  const change = roundHalfUp(now - first, 2);
  return {
    first,
    current: now,
    min: Math.min(...all),
    max: Math.max(...all),
    change,
    changeRate: first > 0 ? roundHalfUp((change / first) * 100, 2) : 0,
  };
}

/**
 * 가로축 눈금 - 날이 바뀌는 첫 점만, 많으면 건너뛰어 maxTicks 개 안쪽으로.
 *
 * 🔴 날짜를 축 값으로 쓰지 않는 이유
 *   한 시간 한 점이라 같은 날짜가 24번 온다. 날짜로 눈금을 잡으면 recharts 가
 *   같은 글자를 여러 번 찍는다(09-13 이 두 번 나왔다). 점마다 유일한 at 을 축 값으로 두고,
 *   그중 날이 바뀌는 점만 눈금으로 고른다.
 *
 * @return 눈금이 될 at 값들
 */
export function dayTicks(rows: FxChartRow[], maxTicks: number): string[] {
  const starts: string[] = [];
  let previous: string | null = null;
  for (const row of rows) {
    // 🔴 첫날은 자정부터 시작할 때만 눈금을 준다 - 7일 전 15시에서 시작하면
    //   첫 눈금과 다음 날 눈금이 몇 시간 간격으로 붙어 글자가 겹쳤다(폰 폭 실측)
    const isFirst = previous === null;
    if (row.day !== previous && (!isFirst || row.at.endsWith('00:00'))) {
      starts.push(row.at);
    }
    previous = row.day;
  }
  if (starts.length <= maxTicks) {
    return starts;
  }
  const step = Math.ceil(starts.length / maxTicks);
  return starts.filter((_, index) => index % step === 0);
}

export interface FxChartRow {
  /** 날짜. "09-19" */
  day: string;
  /** 툴팁 머리. "09-19 14:00" */
  at: string;
  rate: number;
}

/**
 * 추이선 행. 시각 문자열은 KST LocalDateTime 그대로다 - Date 로 파싱하지 않는다
 * (formatDateTime 과 같은 이유 - 브라우저 시간대로 틀어진다).
 */
export function toChartRows(history: ExchangeRateInfo[]): FxChartRow[] {
  return history.map((h) => {
    /*
      🔴 fetchedAt 이 아니라 at 을 쓴다 (2026-09-20, I-66)
        한국은행 백필 행은 fetchedAt 이 전부 "백필을 돌린 날" 이라,
        그 값으로 그리면 과거 1,384일치가 오늘 한 점에 뭉친다.
        at 은 "이 환율이 가리키는 시각" 이라 두 출처가 같은 뜻으로 줄을 선다.
        예전 응답(at 이 없던 때)도 읽을 수 있게 fetchedAt 으로 물러선다.
     */
    const source = h.at ?? h.fetchedAt;
    const match = /^\d{4}-(\d{2}-\d{2})[T ](\d{2}:\d{2})/.exec(source);
    const day = match?.[1];
    const time = match?.[2];
    return {
      day: day ?? source,
      at: day && time ? `${day} ${time}` : source,
      rate: h.rate,
    };
  });
}
